import { AppContext } from 'App.context';
import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import constants from 'utils/constants';
import { notification } from 'antd';

const useDisableLicenceManagerHook = () => {
  const {
    appState,
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);

  const { API } = useContext(ApiContext);

  const [state, setState] = useState({
    loadedLicences: [],
    isAllHotelsSelected: false,
    selectedHotel: getSelectedHotel(),
    fetchLicencesError: false,
    isLicencesLoading: false,
  });

  const isSuperUser = appState.superUser === true;

  useEffect(() => {
    actions.loadLicences();
  }, [state.selectedHotel, state.isAllHotelsSelected]);

  const actions = {
    loadLicences: () => {
      updateState({ isLicencesLoading: true, fetchLicencesError: false });

      const isDisplayableLicence = licence => {
        const validStatusList = [
          constants.LICENCE_END_STATUS.REQUESTED,
          constants.LICENCE_END_STATUS.APPROVED,
          constants.LICENCE_END_STATUS.REACTIVATE_REQUEST,
        ];

        const validStatus = validStatusList.includes(licence?.endStatus);
        const oneYearAgo = Date.now() - 365 * 24 * 60 * 60 * 1000;
        const requestedRecently = licence?.endRequestDate
          ? new Date(licence?.endRequestDate).getTime() >= oneYearAgo
          : true;

        if (isSuperUser) {
          return licence?.endStatus === constants.LICENCE_END_STATUS.REQUESTED;
        }

        return validStatus && requestedRecently;
      };

      const params = state.isAllHotelsSelected
        ? [null, appState.hotelList.map(h => h.id)]
        : [state.selectedHotel?.id];

      API.getHotelLicencesList(...params)
        .then(res => {
          const items = res?.items ?? [];
          updateState({ loadedLicences: items.filter(isDisplayableLicence) });
        })
        .catch(() => {
          updateState({ fetchLicencesError: true });
        })
        .finally(() => {
          updateState({ isLicencesLoading: false });
        });
    },
    handleHotelSelected: hotel => {
      const isAll = !hotel.id && !hotel.key;
      updateState({ loadedLicences: [], isAllHotelsSelected: isAll, selectedHotel: hotel });
    },
    handleAccept: licence => {
      updateState({ isLicencesLoading: true });

      const updateData = {
        reasons: licence.endReason,
        otherReason: licence.endReasonOther,
        hotelsWithLicences: { [licence.hotelId]: [licence.id] },
        status: constants.LICENCE_END_STATUS.APPROVED,
      };

      API.updateHotelLicenceEnd(updateData)
        .then(res => {
          actions.loadLicences();
        })
        .catch(err => {
          updateState({ isLicencesLoading: false });
        });
    },
    handleDeny: licence => {
      updateState({ isLicencesLoading: true });

      const updateData = {
        reasons: licence.endReason,
        otherReason: licence.endReasonOther,
        hotelsWithLicences: { [licence.hotelId]: [licence.id] },
        status: isSuperUser
          ? constants.LICENCE_END_STATUS.DENIED
          : constants.LICENCE_END_STATUS.CANCELED,
      };

      API.updateHotelLicenceEnd(updateData)
        .then(res => {})
        .catch(err => {
          notification.error({
            duration: 6,
            message: translate('accountDetail.disableLicence.disableLicenceError'),
          });
        })
        .finally(() => {
          actions.loadLicences();
        });
    },
    handleRequestReactivation: licence => {
      updateState({ isLicencesLoading: true });

      const updateData = {
        reasons: licence.endReason,
        otherReason: licence.endReasonOther,
        hotelsWithLicences: { [licence.hotelId]: [licence.id] },
        status: constants.LICENCE_END_STATUS.REACTIVATE_REQUEST,
      };

      API.updateHotelLicenceEnd(updateData)
        .then(res => {})
        .catch(err => {
          notification.error({
            duration: 6,
            message: translate('accountDetail.disableLicence.disableLicenceError'),
          });
        })
        .finally(() => {
          actions.loadLicences();
        });
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
};

export default useDisableLicenceManagerHook;
